import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Search, Calendar, ExternalLink, Zap, PenTool, CheckCircle, XCircle, Clock, User, DollarSign } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { apiClient } from '@/services/api';
import { errorLog } from '@/utils/logger';

interface PayoutRequest {
  id: string;
  userId: string;
  totalHours: number;
  totalCoins: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  adminId?: string;
  processedAt?: string;
  rejectionReason?: string;
  approvalMethod?: 'auto' | 'manual';
  approvalReason?: string;
  signatureRequestId?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    fullName: string;
    email: string;
    walletAddress?: string;
  };
  reward?: {
    id: string;
    status: string;
    transactionHash?: string;
    confirmationsReceived?: number;
  };
}

const PayoutHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PayoutRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [approvalMethodFilter, setApprovalMethodFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchHistory();
  }, [statusFilter, approvalMethodFilter, startDate, endDate]);

  const fetchHistory = async () => {
    try {
      setIsLoading(true);

      const params: any = {};

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (approvalMethodFilter !== 'all') {
        params.approvalMethod = approvalMethodFilter;
      }

      if (startDate) {
        params.startDate = new Date(startDate).toISOString();
      }

      if (endDate) {
        params.endDate = new Date(endDate).toISOString();
      }

      const response = await apiClient.payoutRequests.getHistory(params);

      if (response.success && response.data) {
        setRequests(response.data);
      }
    } catch (error: any) {
      errorLog('Failed to fetch payout history:', error);
      toast.error('Failed to load payout history');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRequests = requests.filter((request) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      request.user?.fullName?.toLowerCase().includes(term) ||
      request.user?.email?.toLowerCase().includes(term) ||
      request.id.toLowerCase().includes(term)
    );
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" />
            Paid
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            <Clock className="h-3 w-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="h-3 w-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  const getApprovalBadge = (request: PayoutRequest) => {
    if (!request.approvalMethod) return null;

    if (request.approvalMethod === 'auto') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <Zap className="h-3 w-3" />
          Auto-Approved
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
          <PenTool className="h-3 w-3" />
          Manual Approval
        </span>
      );
    }
  };

  // Calculate summary statistics
  const stats = {
    total: filteredRequests.length,
    totalAmount: filteredRequests.reduce((sum, r) => sum + r.totalCoins, 0),
    autoApproved: filteredRequests.filter(r => r.approvalMethod === 'auto').length,
    manualApproved: filteredRequests.filter(r => r.approvalMethod === 'manual').length,
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/payouts')}
          className="p-2 text-gray-500 hover:text-gray-700 touch-manipulation"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="font-bold text-4xl text-white">Payout History</h1>
          <p className="mt-1 text-lg text-white">View completed payout requests</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, email, or ID..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
            >
              <option value="all">All Statuses</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Approval Method Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Approval Method
            </label>
            <select
              value={approvalMethodFilter}
              onChange={(e) => setApprovalMethodFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
            >
              <option value="all">All Methods</option>
              <option value="auto">Auto-Approved</option>
              <option value="manual">Manual Approval</option>
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <div className="flex gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-900"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-900"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <DollarSign className="h-4 w-4" />
            <span className="text-sm">Total Requests</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <DollarSign className="h-4 w-4" />
            <span className="text-sm">Total Amount</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.totalAmount.toFixed(2)} HEP</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <Zap className="h-4 w-4" />
            <span className="text-sm">Auto-Approved</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.autoApproved}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <PenTool className="h-4 w-4" />
            <span className="text-sm">Manual Approval</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{stats.manualApproved}</p>
        </div>
      </div>

      {/* History Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No history found</h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' || approvalMethodFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Completed payouts will appear here'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Approval
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{request.user?.fullName}</p>
                          <p className="text-sm text-gray-500">{request.user?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-medium text-green-600">{request.totalCoins.toFixed(2)} HEP</p>
                      <p className="text-sm text-gray-500">{request.totalHours.toFixed(2)}h</p>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-4">
                      {getApprovalBadge(request)}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-gray-900">
                        {format(parseISO(request.createdAt), 'MMM d, yyyy')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(parseISO(request.createdAt), 'h:mm a')}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      {request.reward?.transactionHash ? (
                        <Link
                          to={`/explorer?hash=${request.reward.transactionHash}`}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                        >
                          <span className="font-mono">
                            {request.reward.transactionHash.slice(0, 8)}...
                          </span>
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayoutHistoryPage;
